if (!customElements.get('bundle-products')) {
  customElements.define(
    'bundle-products',
    class BundleProducts extends HTMLElement {
      connectedCallback() {
        this.listEl = this.querySelector('[data-bundle-products-list]');
        this.addLabel = this.dataset.addLabel || 'Add';
        this.unitLimit = Number(this.dataset.unitLimit) || 10;
        this.newFlavors = this.parseFlavorList(this.dataset.newFlavors || '');
        this.flavors = this.buildFlavors(this.readVariantsJson());

        if (this.listEl && this.flavors.length) {
          this.render();
        }

        this._onBuilderChange = () => this.syncAllFromState();
        document.addEventListener('bundle-builder:change', this._onBuilderChange);
      }

      disconnectedCallback() {
        document.removeEventListener('bundle-builder:change', this._onBuilderChange);
      }

      parseFlavorList(value) {
        return String(value || '')
          .split(',')
          .map((part) => part.trim().toLowerCase())
          .filter(Boolean);
      }

      isNewFlavor(flavor) {
        const key = String(flavor || '').trim().toLowerCase();
        return Boolean(key) && this.newFlavors.includes(key);
      }

      readVariantsJson() {
        const script = this.querySelector('[data-bundle-variants-json]');
        if (!script) return [];

        try {
          const data = JSON.parse(script.textContent.trim());
          return Array.isArray(data) ? data : [];
        } catch (error) {
          console.error('[bundle-products] invalid variants JSON', error);
          return [];
        }
      }

      /**
       * One row per metafield variant / flavor.
       * Pack size stays fixed (e.g. 20); qty picker adds more of the same variant.
       */
      buildFlavors(variants) {
        const map = new Map();

        variants.forEach((variant) => {
          if (!variant?.id || !variant.flavor) return;

          const flavor = String(variant.flavor).trim();
          if (map.has(flavor)) return;

          const packs = Number(variant.packs) || this.packsFromLabel(variant.sizeTitle);

          map.set(flavor, {
            flavor,
            productId: Number(variant.productId),
            productTitle: variant.productTitle || '',
            description: variant.description || '',
            variant: {
              id: Number(variant.id),
              productId: Number(variant.productId),
              packs,
              sizeTitle: this.formatSizeTitle(variant.sizeTitle, packs),
              image: variant.image || '',
              available: variant.available !== false,
              sellingPlanId: variant.sellingPlanId ? Number(variant.sellingPlanId) : null,
              priceCents: Number(variant.priceCents) || 0,
              compareAtCents: Number(variant.compareAtCents) || Number(variant.priceCents) || 0,
            },
          });
        });

        return Array.from(map.values());
      }

      packsFromLabel(label) {
        const match = String(label || '').match(/(\d+)/);
        return match ? Number(match[1]) : 20;
      }

      formatSizeTitle(label, packs) {
        const value = Number(packs) || this.packsFromLabel(label);
        if (value > 1) return `${value} Stick Packs`;
        return label || '';
      }

      render() {
        this.listEl.innerHTML = this.flavors.map((group) => this.rowHtml(group)).join('');
        this.rows = Array.from(this.querySelectorAll('[data-bundle-product]'));
        this.rows.forEach((row) => this.bindRow(row));
        this.syncAllFromState();
      }

      rowHtml(group) {
        const variant = group.variant;
        const sizeLabel = variant?.sizeTitle || '';
        const title = sizeLabel ? `${group.flavor} - ${sizeLabel}` : group.flavor;
        const isNew = this.isNewFlavor(group.flavor);

        return `
          <li
            class="bundle-products__item"
            data-bundle-product
            data-flavor="${this.escapeAttr(group.flavor)}"
            data-product-id="${group.productId}"
            data-variant-id="${variant?.id || ''}"
            data-title="${this.escapeAttr(title)}"
            data-variant-title="${this.escapeAttr(sizeLabel)}"
            data-packs="${variant?.packs || 20}"
            data-units="0"
            data-quantity="0"
          >
            <div class="bundle-products__media">
              ${
                variant?.image
                  ? `<img src="${this.escapeAttr(variant.image)}" alt="${this.escapeAttr(title)}" width="72" height="72" loading="lazy">`
                  : ''
              }
            </div>
            <div class="bundle-products__content">
              <p class="bundle-products__title">
                <span data-bundle-title-text>${this.escapeHtml(group.flavor)}</span>
                ${isNew ? '<span class="bundle-products__new">NEW</span>' : ''}
              </p>
              ${
                group.description
                  ? `<p class="bundle-products__description">${this.escapeHtml(group.description)}</p>`
                  : ''
              }
              ${
                sizeLabel
                  ? `<p class="bundle-products__size">${this.escapeHtml(sizeLabel)}</p>`
                  : ''
              }
            </div>
            <div class="bundle-products__actions">
              <button type="button" class="bundle-products__add" data-bundle-add>${this.escapeHtml(this.addLabel)}</button>
              <div class="bundle-products__qty-picker" data-bundle-qty-picker hidden>
                <button type="button" class="bundle-products__qty-btn" data-bundle-minus aria-label="Decrease quantity">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
                <span class="bundle-products__qty" data-bundle-qty>1</span>
                <button type="button" class="bundle-products__qty-btn" data-bundle-plus aria-label="Increase quantity">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </li>
        `;
      }

      bindRow(row) {
        row.querySelector('[data-bundle-add]')?.addEventListener('click', () => this.setUnits(row, 1));
        row.querySelector('[data-bundle-minus]')?.addEventListener('click', () => {
          const units = Number(row.dataset.units || 0);
          this.setUnits(row, units - 1);
        });
        row.querySelector('[data-bundle-plus]')?.addEventListener('click', () => {
          const units = Number(row.dataset.units || 0);
          this.setUnits(row, units + 1);
        });
      }

      getFlavorGroup(flavor) {
        return this.flavors.find((group) => group.flavor === flavor);
      }

      /** Total units across all flavors in the builder. */
      getTotalUnits() {
        return window.BundleBuilder?.getTotalQuantity?.() || 0;
      }

      /** Max units this flavor can still hold without exceeding the shared list limit. */
      getMaxUnitsForFlavor(flavor) {
        const currentFlavorUnits = window.BundleBuilder.getFlavorUnits(flavor);
        const otherUnits = this.getTotalUnits() - currentFlavorUnits;
        return Math.max(0, this.unitLimit - otherUnits);
      }

      setUnits(row, units) {
        const flavor = row.dataset.flavor;
        const group = this.getFlavorGroup(flavor);
        if (!group?.variant) return;

        const maxForFlavor = this.getMaxUnitsForFlavor(flavor);
        const nextUnits = Math.max(0, Math.min(maxForFlavor, Number(units) || 0));
        const variant = group.variant;

        window.BundleBuilder.setFlavorUnits(
          flavor,
          nextUnits,
          nextUnits > 0
            ? {
                id: variant.id,
                productId: variant.productId || group.productId,
                title: row.dataset.title,
                variantTitle: variant.sizeTitle,
                packs: variant.packs,
                sellingPlanId: variant.sellingPlanId,
                priceCents: variant.priceCents,
                compareAtCents: variant.compareAtCents,
              }
            : null
        );

        this.syncAllFromState();
      }

      syncAllFromState() {
        const atLimit = this.getTotalUnits() >= this.unitLimit;
        (this.rows || []).forEach((row) => this.syncRow(row, atLimit));
      }

      syncRow(row, atLimit = false) {
        const flavor = row.dataset.flavor;
        const group = this.getFlavorGroup(flavor);
        if (!group?.variant) return;

        const units = window.BundleBuilder.getFlavorUnits(flavor);
        const variant = group.variant;
        const isActive = units > 0;
        const maxForFlavor = this.getMaxUnitsForFlavor(flavor);
        const canIncrease = units < maxForFlavor;

        row.dataset.units = String(units);
        row.dataset.quantity = String(units);
        row.classList.toggle('is-selected', isActive);

        row.dataset.variantId = String(variant.id);
        row.dataset.variantTitle = variant.sizeTitle;
        row.dataset.packs = String(variant.packs);
        row.dataset.title = variant.sizeTitle
          ? `${group.flavor} - ${variant.sizeTitle}`
          : group.flavor;

        const titleEl = row.querySelector('[data-bundle-title-text]');
        if (titleEl) titleEl.textContent = group.flavor;

        const sizeEl = row.querySelector('.bundle-products__size');
        if (sizeEl) sizeEl.textContent = variant.sizeTitle || '';

        const qtyEl = row.querySelector('[data-bundle-qty]');
        if (qtyEl) qtyEl.textContent = String(units || 1);

        const addBtn = row.querySelector('[data-bundle-add]');
        const picker = row.querySelector('[data-bundle-qty-picker]');
        if (addBtn) {
          addBtn.hidden = isActive;
          addBtn.disabled = !isActive && atLimit;
        }
        if (picker) picker.hidden = !isActive;

        const plusBtn = row.querySelector('[data-bundle-plus]');
        if (plusBtn) plusBtn.disabled = !canIncrease;
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      escapeAttr(value) {
        return this.escapeHtml(value).replace(/'/g, '&#39;');
      }
    }
  );
}
